import { Injectable, signal } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { Router } from "@angular/router";
import { BehaviorSubject, forkJoin, Observable, of, throwError } from "rxjs";
import { AppConfig } from "../config/app.config";
import { BasicUserDetails, GetUserDetails } from "../models/sessionStorage.model";
import { LoginDetailsCacheDto } from "../models/login-cache.model";
import {
  LOGIN_AUTH_BOOTSTRAP_KEYS,
  LoginDetailsStorageService,
} from "../services/login-details-storage.service";
import { TempDetailsStorageService } from "../services/temp-details-storage.service";
import { TempCacheDto } from "../models/temp-cache.model";
import { IndicationDetailsStorageService } from "../services/indication-details-storage.service";
import { IndicationCacheDto } from "../models/indication-cache.model";
import { QuoteDetailsStorageService } from "../services/quote-details-storage.service";
import { QuoteCacheDto } from "../models/quote-cache.model";
import { PolicyDetailsStorageService } from "../services/policy-details-storage.service";
import { PolicyCacheDto } from "../models/policy-cache.model";
import { SessionCacheApiService } from "../services/session-cache-api.service";
import {
  SessionCacheHydrationResult,
  SessionCacheHydrationService,
} from "../services/session-cache-hydration.service";
import { parseSessionJson } from "../utils/session-json.util";
import Cookies from 'js-cookie';

interface LoginRequest {
  userMaster: {
    userEmail: string;
  };
  password: string;
}

interface newTokenResponse {
  jwtToken: string;
  refreshToken: string;
}
@Injectable({
  providedIn: "root",
})
export class AuthService {
  httpClient = inject(HttpClient);
  loginDetailsStorage = inject(LoginDetailsStorageService);
  tempDetailsStorage = inject(TempDetailsStorageService);
  indicationDetailsStorage = inject(IndicationDetailsStorageService);
  quoteDetailsStorage = inject(QuoteDetailsStorageService);
  policyDetailsStorage = inject(PolicyDetailsStorageService);
  sessionCacheApi = inject(SessionCacheApiService);
  sessionCacheHydration = inject(SessionCacheHydrationService);
  baseUrl = "http://localhost:3000/api";
  router = inject(Router);
  private apiUrl = AppConfig.apiUrl;
  // loading = signal(false);

  constructor() {}

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  private loadingPins = 0;
  private cacheApiLoadingCount = 0;

  signup(data: any) {
    return this.httpClient.post(`${this.baseUrl}/register`, data);
  }

  //   login(userEmail: string, password: string): Observable<any> {
  //     // this.loading.set(true);
  //     const loginUrl = `${this.apiUrl}/Home/login?emailid=${encodeURIComponent(userEmail)}&password=${encodeURIComponent(password)}`;
  //     const body = {
  //         userMaster: {
  //             userEmail: userEmail
  //         },
  //         password: password
  //     };
  //     return this.httpClient.post(loginUrl, body).pipe(
  //       // tap(() => {
  //       //     this.loading.set(false);
  //       // }),
  //       catchError((error: HttpErrorResponse) => {
  //           // this.loading.set(false);
  //           return throwError(() => error);
  //       })
  //   );
  // }
  login(email: string, password: string): Observable<any> {
    const path = `/api/Home/login?emailid=${email}&password=${password}`
    const encodedPath = encodeURIComponent(path);
    const url = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    const body = {
      userMaster: { userEmail: email },
      password: password
    };
    return this.httpClient.post<any>(url, body).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  // Utility function to convert Base64 to a data URL for the image
  convertBase64ToImage(base64String: string): string {
    return `data:image/png;base64,${base64String}`;
  }

  /** Spinner can be pinned during bootstrap/restore to prevent flicker. */
  pinLoading(): void {
    this.loadingPins += 1;
    this.loadingSubject.next(true);
  }

  unpinLoading(): void {
    this.loadingPins = Math.max(0, this.loadingPins - 1);
    if (this.loadingPins === 0 && this.cacheApiLoadingCount === 0) {
      this.loadingSubject.next(false);
    }
  }

  /** Tracks in-flight session cache GET calls (refresh restore). */
  beginCacheApiLoading(): void {
    this.cacheApiLoadingCount += 1;
    this.loadingSubject.next(true);
  }

  endCacheApiLoading(): void {
    this.cacheApiLoadingCount = Math.max(0, this.cacheApiLoadingCount - 1);
    if (this.cacheApiLoadingCount === 0) {
      this.setLoading(false);
    }
  }

  isCacheApiLoading(): boolean {
    return this.cacheApiLoadingCount > 0;
  }

  setLoading(loading: boolean): void {
    if (!loading && (this.loadingPins > 0 || this.cacheApiLoadingCount > 0)) {
      return;
    }
    this.loadingSubject.next(loading);
  }

  loading(): boolean {
    return this.loadingSubject.getValue();
  }

  getRole(): number | null {
    const role = this.loginDetailsStorage.getItem("role");
    return role ? parseInt(role, 10) : null;
  }

  sendOtp(email: string): Observable<any> {
    const path = `/api/send-otp?email=${email}`
    const encodedPath = encodeURIComponent(path);
    const otpUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    return this.httpClient.get(otpUrl).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  verifyOtp(userId: number, otp: number): Observable<any> {
    const path = `/api/Home/VerifyOtp?userId=${userId}&otp=${otp}`
    const encodedPath = encodeURIComponent(path);
    const verifyOtpUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    const payload = { userId, otp }; // Payload for POST request
    return this.httpClient.post(verifyOtpUrl, payload);
  }

  resendOtp(email: string): Observable<any> {
    const path = `/api/resend-otp?email=${email}`
    const encodedPath = encodeURIComponent(path);
    const resendOtpUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    return this.httpClient.get(resendOtpUrl).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  getNewToken(token: string ,refreshToken: string) {
    const path = `/api/Home/refresh`
    const encodedPath = encodeURIComponent(path);
    const tokenurl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    const payload = {jwtToken: token, refreshToken: refreshToken}
    return this.httpClient.post<newTokenResponse>(tokenurl, payload).pipe(
      map((response) => response)
    );
  }

  logout(token: string, userId: number, isExplicitLogout = true) {
    const path = `/api/Home/Logout?userId=${userId}&isExplicitLogout=${isExplicitLogout}`
    const encodedPath = encodeURIComponent(path);
    const logoutUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    return this.httpClient.post<HttpResponse<any>>(logoutUrl, null, 
      { observe: 'response', responseType: 'text' as 'json' })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }


  logoutOnTabClose(
    token: string,
    userId: number,
    isExplicitLogout = false
  ): void {

   const path = `/api/Home/Logout?userId=${userId}&isExplicitLogout=${isExplicitLogout}`
    const encodedPath = encodeURIComponent(path);
    const logoutUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;

   // const logoutUrl = `${this.apiUrl}/Home/Logout?userId=${userId}&isExplicitLogout=${isExplicitLogout}`;

    navigator.sendBeacon(logoutUrl, null);

    setTimeout(() => {
      this.cleanUpStorageData().subscribe();
    }, 50);
  }
  
  cleanUpStorageData(): Observable<void> {
    const finalizeCleanup = (): void => {
      this.clearLocalAuthStorage();
    };

    const sessionId = this.getCacheSessionId();
    if (sessionId) {
      return this.sessionCacheApi.removeSession(sessionId).pipe(
        catchError(() => of(null)),
        map(() => {
          finalizeCleanup();
        })
      );
    }

    finalizeCleanup();
    return of(undefined);
  }

  private clearLocalAuthStorage(): void {
    this.sessionCacheHydration.reset();
    this.loginDetailsStorage.clear();
    this.tempDetailsStorage.clear();
    this.indicationDetailsStorage.clear();
    this.quoteDetailsStorage.clear();
    this.policyDetailsStorage.clear();
    if (typeof sessionStorage !== "undefined") {
      localStorage.clear();
      sessionStorage.clear();
    }
    this.purgeLegacyAuthCookies();
  }

  /** One-time migration from legacy js-cookie auth fields. */
  migrateLegacyAuthCookiesToStorage(): void {
    if (typeof window === "undefined") {
      return;
    }

    const legacyToken = Cookies.get("token");
    const legacyUserId = Cookies.get("userId");
    const legacySessionId = Cookies.get("sessionId");
    const legacyLastRoute = Cookies.get("lastVisitedRoute");
    const legacyLogoutFlag = Cookies.get("isLogoutClicked");

    if (legacyToken && !this.loginDetailsStorage.getItem("token")) {
      this.loginDetailsStorage.setItem("token", legacyToken);
    }
    if (
      legacyToken &&
      typeof sessionStorage !== "undefined" &&
      !sessionStorage.getItem(LOGIN_AUTH_BOOTSTRAP_KEYS.authAccessToken)
    ) {
      sessionStorage.setItem(
        LOGIN_AUTH_BOOTSTRAP_KEYS.authAccessToken,
        legacyToken
      );
    }
    if (legacyUserId && !this.loginDetailsStorage.getItem("userId")) {
      this.loginDetailsStorage.setItem("userId", String(legacyUserId));
    }
    if (legacySessionId && !this.loginDetailsStorage.getItem("sessionId")) {
      this.loginDetailsStorage.setItem("sessionId", String(legacySessionId));
    }
    if (
      legacyToken &&
      (legacySessionId || legacyUserId) &&
      typeof sessionStorage !== "undefined" &&
      !sessionStorage.getItem("cacheSessionId")
    ) {
      sessionStorage.setItem(
        "cacheSessionId",
        String(legacySessionId ?? legacyUserId)
      );
    }
    if (
      legacyLastRoute &&
      typeof sessionStorage !== "undefined" &&
      !sessionStorage.getItem("lastVisitedRoute")
    ) {
      sessionStorage.setItem("lastVisitedRoute", legacyLastRoute);
    }
    if (
      legacyLogoutFlag &&
      typeof sessionStorage !== "undefined" &&
      !sessionStorage.getItem("isLogoutClicked")
    ) {
      sessionStorage.setItem("isLogoutClicked", legacyLogoutFlag);
    }

    if (legacyToken || legacyUserId || legacySessionId) {
      this.purgeLegacyAuthCookies();
    }
  }

  purgeLegacyAuthCookies(): void {
    Cookies.remove("userId");
    Cookies.remove("token");
    Cookies.remove("sessionId");
    Cookies.remove("lastVisitedRoute");
    Cookies.remove("isLogoutClicked");
  }

  getCacheSessionId(): string | null {
    return this.loginDetailsStorage.getCacheSessionId();
  }

  setLoginDetailsCache(
    overrides: Partial<LoginDetailsCacheDto> = {},
    sessionIdOverride?: string | number
  ): Observable<LoginDetailsCacheDto | null> {
    return this.loginDetailsStorage.saveToCache(overrides, sessionIdOverride);
  }

  restoreLoginDetailsFromCache(): Observable<boolean> {
    return this.loginDetailsStorage.loadFromCache();
  }

  waitForSessionCachesRestored(): Observable<SessionCacheHydrationResult> {
    return this.sessionCacheHydration.ensureRestored();
  }

  restoreTempDetailsFromCache(): Observable<boolean> {
    return this.tempDetailsStorage.loadFromCache();
  }

  persistTempDetails(
    tempId: number | string,
    tempNumber: string
  ): Observable<TempCacheDto | null> {
    return this.tempDetailsStorage.persistTempDetails(tempId, tempNumber);
  }

  isTempCacheHydrated(): boolean {
    return this.tempDetailsStorage.isLoaded();
  }

  restoreIndicationDetailsFromCache(): Observable<boolean> {
    return this.indicationDetailsStorage.loadFromCache();
  }

  persistIndicationDetails(
    overrides: Partial<IndicationCacheDto> = {}
  ): Observable<IndicationCacheDto | null> {
    return this.indicationDetailsStorage.saveToCache(overrides);
  }

  isIndicationCacheHydrated(): boolean {
    return this.indicationDetailsStorage.isLoaded();
  }

  restoreQuoteDetailsFromCache(): Observable<boolean> {
    return this.quoteDetailsStorage.loadFromCache();
  }

  persistQuoteDetails(
    overrides: Partial<QuoteCacheDto> = {}
  ): Observable<QuoteCacheDto | null> {
    return this.quoteDetailsStorage.saveToCache(overrides);
  }

  isQuoteCacheHydrated(): boolean {
    return this.quoteDetailsStorage.isLoaded();
  }

  restorePolicyDetailsFromCache(): Observable<boolean> {
    return this.policyDetailsStorage.loadFromCache();
  }

  persistPolicyDetails(
    overrides: Partial<PolicyCacheDto> = {}
  ): Observable<PolicyCacheDto | null> {
    return this.policyDetailsStorage.saveToCache(overrides);
  }

  isPolicyCacheHydrated(): boolean {
    return this.policyDetailsStorage.isLoaded();
  }

  /**
   * DELETE workflow cache sections, clear in-memory workflow stores,
   * then run optional callback (e.g. legacy sessionStorage key cleanup).
   */
  resetWorkflowCaches(
    onComplete?: () => void,
    options: { includeTemp?: boolean } = {}
  ): void {
    const includeTemp = options.includeTemp ?? true;
    const stores = includeTemp
      ? [
          this.tempDetailsStorage,
          this.indicationDetailsStorage,
          this.quoteDetailsStorage,
          this.policyDetailsStorage,
        ]
      : [
          this.indicationDetailsStorage,
          this.quoteDetailsStorage,
          this.policyDetailsStorage,
        ];

    stores.forEach((store) => store.disablePersist());

    const finish = () => {
      stores.forEach((store) => store.clear());
      onComplete?.();
      stores.forEach((store) => store.enablePersist());
    };

    const sessionId = this.getCacheSessionId();
    if (!sessionId) {
      finish();
      return;
    }

    const removeCalls = includeTemp
      ? [
          this.tempDetailsStorage.removeFromCache(sessionId),
          this.indicationDetailsStorage.removeFromCache(sessionId),
          this.quoteDetailsStorage.removeFromCache(sessionId),
          this.policyDetailsStorage.removeFromCache(sessionId),
        ]
      : [
          this.indicationDetailsStorage.removeFromCache(sessionId),
          this.quoteDetailsStorage.removeFromCache(sessionId),
          this.policyDetailsStorage.removeFromCache(sessionId),
        ];

    forkJoin(removeCalls).subscribe({
      next: () => finish(),
      error: () => finish(),
    });
  }

  isLoginCacheHydrated(): boolean {
    return this.loginDetailsStorage.isLoaded();
  }

  persistLoginDetailsAfterSignIn(params: {
    token: string;
    refreshToken?: string;
    userDetails: Record<string, unknown>;
    userMaster: Record<string, unknown>;
    userRole: number;
    userId: number;
    branchId?: number | string;
    companyId?: number | string;
    userEmail?: string;
    createdUserId?: number | string;
    updatedUserId?: number | string;
    approvedStates?: number[];
    approvedProducts?: number[];
  }): Observable<LoginDetailsCacheDto | null> {
    const approvedStates = params.approvedStates?.length
      ? params.approvedStates.map((id) => String(id)).join(",")
      : "";
    const approvedProducts = params.approvedProducts?.length
      ? params.approvedProducts.map((id) => String(id)).join(",")
      : "";

    return this.setLoginDetailsCache(
      {
        token: params.token,
        userId: params.userId,
        userDetails: JSON.stringify(params.userDetails),
        userMaster: JSON.stringify(params.userMaster),
        role: String(params.userRole),
        branchId: this.loginDetailsStorage.toCacheInt(params.branchId),
        companyId: this.loginDetailsStorage.toCacheInt(params.companyId),
        createdUserId: this.loginDetailsStorage.toCacheInt(params.createdUserId),
        updatedUserId: this.loginDetailsStorage.toCacheInt(params.updatedUserId),
        userEmail: params.userEmail ?? "",
        refreshToken: params.refreshToken ?? "",
        approvedStates,
        approvedProducts,
      },
      params.userId
    ).pipe(tap(() => this.sessionCacheHydration.markHydrated(true)));
  }

  persistLoginDetailsAfterOtp(params: {
    sessionId: number | string;
    userLATInterval: number | string;
  }): Observable<LoginDetailsCacheDto | null> {
    return this.setLoginDetailsCache(
      {
        sessionId: params.sessionId,
        userLATInterval: this.loginDetailsStorage.toCacheInt(
          params.userLATInterval
        ),
      },
      params.sessionId
    ).pipe(tap(() => this.sessionCacheHydration.markHydrated(true)));
  }

  setToken(token: string): void {
    this.loginDetailsStorage.setItem("token", token);
    this.setSessionActive();
  }

  setUserId(userId: string | number): void {
    this.loginDetailsStorage.setItem("userId", String(userId));
  }

  setRefreshToken(refreshToken: string): void {
    this.loginDetailsStorage.setItem("refreshToken", refreshToken);
  }

  setSessionActive() {
    sessionStorage.setItem("lastSessionActive", "true"); // Mark session as active
  }

  setLastVisitedRoute(url: string): void {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("lastVisitedRoute", url);
    }
  }

  setIsLogoutClicked(value: boolean): void {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("isLogoutClicked", value ? "true" : "false");
    }
  }

  getIsLogoutClicked(): boolean {
    if (typeof sessionStorage === "undefined") {
      return false;
    }
    const value = sessionStorage.getItem("isLogoutClicked");
    return value === "true" || value === '"true"';
  }

  setIsOtpVerified() {
    sessionStorage.setItem("isOTPVerified", "true");
  }

  setApprovedStatesId(stateIds: any[]) {
    this.loginDetailsStorage.setItem("approvStatesIds", JSON.stringify(stateIds));
  }

  setApprovedProductsId(productIds: any[]) {
    this.loginDetailsStorage.setItem(
      "approvProductIds",
      JSON.stringify(productIds)
    );
  }

  getApprovedStatesIdsFromStorage() {
    const getApprovStatesIds = this.loginDetailsStorage.getItem("approvStatesIds");
    const approvStatesIds = getApprovStatesIds
      ? JSON.parse(getApprovStatesIds)
      : [];
    return approvStatesIds;
  }

  getApprovedProductIdsFromStorage() {
    const getApprovProductIds =
      this.loginDetailsStorage.getItem("approvProductIds");
    const approvProductIds = getApprovProductIds
      ? JSON.parse(getApprovProductIds)
      : [];
    return approvProductIds;
  }

  getIsOTPVerified(): boolean {
    if (typeof sessionStorage !== "undefined") {
      const isOTPVerified = sessionStorage.getItem("isOTPVerified");
      return isOTPVerified === "true" && this.isAuthenticated();
    }
    return false;
  }

  getToken(): string | null {
    return this.loginDetailsStorage.getItem("token");
  }

  getUserId(): string | null {
    return this.loginDetailsStorage.getItem("userId");
  }

  /** Restores JWT from encrypted sessionStorage refresh token after page refresh. */
  bootstrapSessionFromRefreshToken(): Observable<boolean> {
    if (this.getToken()) {
      return of(true);
    }

    const refreshToken =
      this.getRefreshToken() ??
      this.loginDetailsStorage.getBootstrapRefreshToken();
    if (!refreshToken) {
      return of(false);
    }

    return this.getNewToken("", refreshToken).pipe(
      map((newToken) => {
        this.setToken(newToken.jwtToken);
        this.setRefreshToken(newToken.refreshToken);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  getRefreshToken(): string | null {
    return this.loginDetailsStorage.getItem("refreshToken");
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  hasRestorableSession(): boolean {
    if (this.getIsLogoutClicked()) {
      return false;
    }
    return !!(
      this.getToken() ||
      this.loginDetailsStorage.hasBootstrapAccessToken() ||
      this.loginDetailsStorage.getBootstrapRefreshToken() ||
      this.loginDetailsStorage.getBootstrapSessionId()
    );
  }

  restoreSession(): boolean {
    if (typeof sessionStorage !== "undefined") {
      const sessionActive = sessionStorage.getItem("lastSessionActive");
      return sessionActive === "true" && this.isAuthenticated();
    }
    return false;
  }

  getLastVisitedRoute(): string | null {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage.getItem("lastVisitedRoute");
  }

  validatePasswordLink(key: string) {
    const params = new HttpParams().set('key', key);
    const path = `/api/Home/ValidateTime?`
    const encodedPath = encodeURIComponent(path)+'key='+ key;
    const validateLinkUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    return this.httpClient.get(validateLinkUrl, {responseType: 'text' }).pipe(
      map(response => {
        try {
          return { success: true, message: response } // Try to parse as JSON
        } catch (e) {
          return { success: false, message: response }; // If it fails, return the plain text
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // You can return a structured error response as well
        return of({ success: false, message: error.error });
      })
    );
  }

  forgotPassword(email: string): Observable<any> {
    const path = `/api/Home/SendResetPasswordMail?mailId=${email}`
    const encodedPath = encodeURIComponent(path);
    const forgotPasswordUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    return this.httpClient.post(forgotPasswordUrl, { email }, { responseType: 'text' }).pipe(
      tap(() => {
        // this.loading.set(false); 
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  resetPassword(email: string, newPassword: string, isCreateFlag: string): Observable<string> {
    
    const path = `/api/Home/ResetPassword?emailid=${email}&password=${newPassword}&isCreate=${isCreateFlag}`
    const encodedPath = encodeURIComponent(path);
    const resetPasswordUrl = `${this.apiUrl}/Proxy?path=${encodedPath}`;
    return this.httpClient.post(resetPasswordUrl, { email, newPassword }, { responseType: 'text' }).pipe(
      //   tap(() => {
      //     this.loading.set(false); 
      // }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  isUwAdmin(): boolean {
    const userRole = this.getUserDetailsFromStorage()?.userRole;
    return userRole === "2";
  }

  isSysAdmin(): boolean {
    const userRole = this.getUserDetailsFromStorage()?.userRole;
    return userRole === "3";
  }

  isBroker(): boolean {
    const userRole = this.getUserDetailsFromStorage()?.userRole;
    return userRole === "1";
  }

  isPolicyType(): boolean {
    const policyType = this.getUserDetailsFromStorage()?.isPolicyType;
    if (policyType) {
      return policyType === "true";
    } else {
      return false;
    }
  }

  /** Avoids `Number(null)` → 0 when policy id is not loaded yet. */
  getPolicyIdAsNumber(): number | null {
    const raw = this.getUserDetailsFromStorage()?.policyId;
    if (raw == null || raw === "" || raw === "0") {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  getUserDetailsFromStorage(): GetUserDetails {
    const storedUserDetails = this.loginDetailsStorage.getItem("userDetails");
    const emptyUserDetails: BasicUserDetails = {
      email: null,
      firstName: null,
      fullName: null,
      lastName: null,
      roleName: null,
      userId: null,
    };
    const userDetails = storedUserDetails
      ? parseSessionJson<BasicUserDetails | null>(storedUserDetails, null) ??
        emptyUserDetails
      : emptyUserDetails;
    const userRole = this.loginDetailsStorage.getItem("role");
    const branchId = this.loginDetailsStorage.getItem("branchId");
    const companyId = this.loginDetailsStorage.getItem("companyId");
    const indicationId = sessionStorage.getItem("indicationId");
    const quoteId = sessionStorage.getItem("quoteId");
    const quoteType = sessionStorage.getItem("quoteType");
    const isPolicyType = sessionStorage.getItem("isPolicyType");
    const createdUserId = this.loginDetailsStorage.getItem("createdUserId");
    const updatedUserId = this.loginDetailsStorage.getItem("updatedUserId");
    const quoteReferralLock = sessionStorage.getItem("quoteReferralLock");
    const Id = sessionStorage.getItem("id");
    const isAmendByUWAdmin = sessionStorage.getItem("isAmendByUWAdmin");
    const policyId = sessionStorage.getItem("policyId");
    const getQuoteNumber = sessionStorage.getItem('quoteNumber');
    const response: GetUserDetails = {
      userDetails,
      userRole,
      branchId,
      companyId,
      indicationId,
      quoteId,
      quoteType,
      createdUserId,
      updatedUserId,
      Id,
      isAmendByUWAdmin,
      isPolicyType,
      quoteReferralLock,
      policyId,
      getQuoteNumber,
    };

    return response;
  }
}
